<!-- Canvas fills padded router outlet; single frame inside parent -->
<div class="workflow-root" [class.workflow-root--fullscreen]="fullscreen()">
    <div
        class="workflow-canvas-shell flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200/90 bg-slate-100 dark:border-gray-700/90 dark:bg-gray-950/85">

        <!-- Toolbar / actions (inside canvas, top) -->
        <div
            class="workflow-canvas-toolbar shrink-0 flex flex-wrap items-center justify-between gap-3 px-3 py-2.5 border-b border-gray-200/80 dark:border-gray-700/80 bg-white/85 dark:bg-gray-900/85 backdrop-blur-sm z-20">
            <div class="min-w-0">
                <h2 class="text-base font-semibold text-gray-900 dark:text-white truncate">Workflow</h2>
                <p class="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                    Start is <strong class="font-medium text-violet-600 dark:text-violet-400">purple</strong> · drag
                    <strong class="font-medium text-violet-600 dark:text-violet-400">violet</strong> to a
                    <strong class="font-medium text-emerald-600 dark:text-emerald-400">green</strong> input or empty
                    canvas to add agents · Pan / zoom: wheel
                </p>
            </div>
            <div class="flex flex-wrap items-center gap-2">
                <button type="button"
                    class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-100 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                    (click)="saveWorkflow($event)">
                    <app-icon-v2 name="Save" [size]="14" />
                    Save
                </button>
                <div class="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-0.5 hidden sm:block"></div>
                <button type="button"
                    class="inline-flex items-center justify-center p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    (click)="zoomOut($event)" title="Zoom out">
                    <app-icon-v2 name="minus" [size]="16" />
                </button>
                <button type="button"
                    class="inline-flex items-center justify-center p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    (click)="zoomIn($event)" title="Zoom in">
                    <app-icon-v2 name="plus" [size]="16" />
                </button>
                <button type="button"
                    class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    (click)="resetView($event)">
                    <app-icon-v2 name="Scan" [size]="14" />
                    Reset view
                </button>
                <div class="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-2 hidden sm:block"></div>
                <button type="button"
                    class="inline-flex items-center justify-center p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    [title]="fullscreen() ? 'Exit full view' : 'Full view'"
                    [attr.aria-label]="fullscreen() ? 'Exit full view' : 'Full view'"
                    (click)="toggleFullscreen($event)">
                    @if (fullscreen()) {
                    <app-icon-v2 name="minimize2" [size]="16" />
                    } @else {
                    <app-icon-v2 name="maximize2" [size]="16" />
                    }
                </button>
            </div>
        </div>

        <div #viewport class="workflow-viewport relative flex-1 min-h-0 w-full overflow-hidden select-none"
            [class.workflow-viewport--mod-key]="modMultiSelectKeyDown()" [attr.aria-busy]="workflowGraphLoading()"
            (wheel)="onWheel($event)" (pointerdown)="startPan($event)" (click)="clearCanvasSelection()">

            @if (workflowGraphLoading()) {
            <div
                class="absolute inset-0 z-40 flex flex-col items-center justify-center gap-3 bg-slate-100/90 dark:bg-gray-950/90 backdrop-blur-[3px]"
                (wheel)="$event.preventDefault(); $event.stopPropagation()"
                (pointerdown)="$event.stopPropagation()" role="status" aria-live="polite" aria-label="Loading workflow">
                <div
                    class="h-10 w-10 rounded-full border-[3px] border-violet-200 border-t-violet-600 dark:border-violet-800 dark:border-t-violet-400 animate-spin">
                </div>
                <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Loading workflow…</span>
            </div>
            }

            <div
                class="absolute top-2 left-2 z-10 text-[10px] text-gray-400 dark:text-gray-500 pointer-events-none max-w-[min(100%,18rem)]">
                Wheel pans · ⌃/⌘ + wheel zooms · ⌃/⌘ + click nodes to multi-select · ⌃/⌘ + drag empty area to box-select
            </div>

            @if (draggingLinkSourceId()) {
            <div
                class="pointer-events-none absolute bottom-3 left-1/2 z-30 w-[min(100%,22rem)] -translate-x-1/2 px-2 text-center text-[11px] leading-snug text-purple-800 dark:text-purple-200">
                Release on a green input to connect, or on empty canvas to create a new agent. Esc to cancel.
            </div>
            }

            @if (selectedEdgeId() && !propertiesPanelOpen()) {
            <div
                class="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 rounded-lg bg-gray-900/85 dark:bg-gray-100/90 text-white dark:text-gray-900 text-xs shadow-lg pointer-events-none">
                Edge selected · Backspace to delete
            </div>
            }

            <div class="workflow-world absolute left-0 top-0" [style.width.px]="worldWidth"
                [style.height.px]="worldHeight" [style.transform]="worldTransformStyle()">

                <svg class="absolute inset-0 overflow-visible pointer-events-none z-[1]" [attr.width]="worldWidth"
                    [attr.height]="worldHeight">
                    <defs>
                        <marker id="wf-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                            <path d="M0,0 L0,8 L8,4 z" class="fill-purple-500 dark:fill-purple-400" />
                        </marker>
                    </defs>
                    @for (edge of edges(); track edge.id) {
                    <g data-workflow-edge class="pointer-events-auto transition-opacity duration-200"
                        [class.opacity-30]="isEdgeDimmed(edge)">
                        <path [attr.d]="edgePath(edge)" fill="none" stroke="transparent" stroke-width="14"
                            class="pointer-events-stroke cursor-pointer" (click)="selectEdge(edge.id, $event)" />
                        <path [attr.d]="edgePath(edge)" fill="none"
                            [class.stroke-purple-500]="selectedEdgeId() !== edge.id"
                            [class.dark:stroke-purple-400]="selectedEdgeId() !== edge.id"
                            [class.stroke-indigo-500]="selectedEdgeId() === edge.id"
                            [class.dark:stroke-indigo-300]="selectedEdgeId() === edge.id" stroke-width="2"
                            stroke-dasharray="6 4" marker-end="url(#wf-arrow)" class="pointer-events-none" />
                    </g>
                    }
                    @if (marqueeRectWorld(); as mr) {
                    <rect [attr.x]="mr.x" [attr.y]="mr.y" [attr.width]="mr.w" [attr.height]="mr.h"
                        fill="rgb(139 92 246 / 0.12)" stroke="rgb(139 92 246 / 0.65)" stroke-width="1"
                        stroke-dasharray="4 3" rx="2" ry="2" class="pointer-events-none z-[4]" />
                    }
                    @if (draggingLinkSourceId()) {
                    <path [attr.d]="linkPreviewPath()" fill="none" stroke="rgb(168 85 247)" stroke-width="2"
                        stroke-dasharray="6 4" class="dark:stroke-purple-400 pointer-events-none z-[2]" />
                    }
                </svg>

                @for (node of nodes(); track node.id) {
                <div class="absolute z-[5] flex flex-col overflow-visible transition-opacity duration-200"
                    [class.wf-node-dimmed]="isNodeDimmed(node.id)" [style.left.px]="node.x"
                    [style.top.px]="node.y - NODE_CHROME_OFFSET_PX" [style.width.px]="NODE_W">

                    <div data-wf-node-toolbar class="flex shrink-0 items-center justify-end gap-1.5 pb-2"
                        (pointerdown)="$event.stopPropagation()" (click)="$event.stopPropagation()">
                        @if (node.kind === 'START') {
                        <button type="button"
                            class="wf-node-toolbar-btn wf-path-highlight-btn wf-path-highlight-btn--start inline-flex h-7 min-w-[1.75rem] items-center justify-center gap-1 rounded-md border px-2 text-white shadow-sm"
                            [class.wf-path-highlight-btn--on]="isPathHighlightActiveForNode(node.id)"
                            title="Highlight connections to this node (click again to turn off)"
                            (click)="togglePathHighlight(node.id, $event)">
                            <app-icon-v2 name="route" [size]="14" />
                        </button>
                        <button type="button"
                            class="wf-node-toolbar-btn inline-flex h-7 min-w-[1.75rem] items-center justify-center gap-1 rounded-md border border-violet-400/80 bg-violet-600 px-2 text-white shadow-sm hover:bg-violet-500 dark:border-violet-400 dark:bg-violet-600 dark:hover:bg-violet-500"
                            title="Node settings" (click)="openNodeProperties(node.id, $event)">
                            <app-icon-v2 name="settings" [size]="14" />
                        </button>
                        } @else if (node.workflow) {
                        <button type="button"
                            class="wf-node-toolbar-btn wf-path-highlight-btn wf-path-highlight-btn--agent inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-md border px-2 text-gray-700 shadow-sm dark:text-gray-200"
                            [class.wf-path-highlight-btn--on]="isPathHighlightActiveForNode(node.id)"
                            title="Highlight connections to this node (click again to turn off)"
                            (click)="togglePathHighlight(node.id, $event)">
                            <app-icon-v2 name="route" [size]="14" />
                        </button>
                        <button type="button"
                            class="wf-node-toolbar-btn inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-md border border-gray-200 bg-white px-2 text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700/80"
                            title="Node settings" (click)="openNodeProperties(node.id, $event)">
                            <app-icon-v2 name="settings" [size]="14" />
                        </button>
                        <button type="button"
                            class="wf-node-toolbar-btn inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-md border border-gray-200 bg-white px-2 text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700/80"
                            title="Duplicate node" (click)="copyWorkflowNode(node.id, $event)">
                            <app-icon-v2 name="copy" [size]="14" />
                        </button>
                        }
                    </div>

                    <div data-node-card [attr.data-node-id]="node.id"
                        class="group relative flex flex-col overflow-visible rounded-lg transition-shadow wf-node-card-canvas"
                        [class.wf-node-card-canvas--mod-key]="modMultiSelectKeyDown()"
                        [class.wf-node-start]="node.kind === 'START'"
                        [class.wf-node-workflow]="node.kind === 'WORKFLOW'" [class.ring-2]="isNodeSelected(node.id)"
                        [class.ring-purple-500]="isNodeSelected(node.id)"
                        [class.dark:ring-purple-400]="isNodeSelected(node.id)" [style.width.px]="NODE_W"
                        [style.height.px]="NODE_H" (pointerdown)="onNodePointerDown(node, $event)"
                        (click)="$event.stopPropagation()">

                        @if (node.kind !== 'START') {
                        @for (h of getNodeInputHandles(node); track $index) {
                        <button type="button" tabindex="-1" data-wf-input-handle [attr.data-node-id]="node.id"
                            class="absolute z-30 h-3 w-3 cursor-default rounded-full border-2 border-white bg-emerald-500 shadow ring-1 ring-black/20 hover:bg-emerald-400 dark:border-gray-950 touch-none"
                            [ngStyle]="h.style" [class.ring-2]="linkHoverTargetId() === node.id"
                            [class.ring-amber-300]="linkHoverTargetId() === node.id"
                            [class.scale-110]="linkHoverTargetId() === node.id" title="Drop connection here"
                            (click)="$event.stopPropagation()"></button>
                        }
                        }

                        <button type="button" tabindex="-1" data-wf-output-handle [attr.data-node-id]="node.id"
                            class="wf-connect-handle wf-connect-handle--out absolute right-0 top-1/2 z-30 flex h-6 w-6 translate-x-1/2 -translate-y-1/2 cursor-default items-center justify-center rounded-full border-2 border-white bg-violet-500 shadow-md ring-1 ring-black/20 transition-opacity duration-150 hover:bg-violet-400 dark:border-gray-950 touch-none opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"
                            [class.opacity-100]="draggingLinkSourceId() === node.id"
                            [class.pointer-events-auto]="draggingLinkSourceId() === node.id"
                            [class.ring-2]="draggingLinkSourceId() === node.id"
                            [class.ring-amber-300]="draggingLinkSourceId() === node.id"
                            title="Drag to connect or release on empty canvas for a new agent"
                            (pointerdown)="onOutputHandlePointerDown(node.id, $event)"
                            (click)="$event.stopPropagation()">
                            <app-icon-v2 name="arrow-up-right" [size]="16"
                                class="text-white shrink-0 pointer-events-none" />
                        </button>

                        @if (node.kind === 'START') {
                        <div
                            class="wf-node-start-header flex shrink-0 items-center gap-1.5 rounded-t-[inherit] px-2.5 py-2 text-xs font-semibold text-white shadow-sm">
                            <span
                                class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/20 text-[10px]">▶</span>
                            <span class="min-w-0 truncate">{{ node.startMeta?.name ?? 'Start' }}</span>
                        </div>
                        <div
                            class="wf-node-start-content flex flex-1 items-center rounded-b-[inherit] px-2.5 py-1.5 text-[10px] leading-snug">
                            Entry · connect outward
                        </div>
                        } @else if (node.workflow) {
                        <div class="wf-node-workflow-header">
                            <span
                                class="wf-node-workflow-title min-w-0 flex-1 truncate text-[11px] font-semibold leading-tight">{{
                                node.workflow.name }}</span>
                        </div>
                        <div class="wf-node-workflow-body line-clamp-2">
                            @if (node.workflow.systemPrompt) {
                            {{ node.workflow.systemPrompt }}
                            } @else {
                            <span class="text-gray-400 dark:text-gray-500 italic">No system prompt configured.</span>
                            }
                        </div>
                        }
                    </div>
                </div>
                }
            </div>
        </div>
    </div>

    <!-- Properties: slide-in panel; backdrop + drawer use Tailwind (below app navbar unless fullscreen) -->
    <div class="fixed bg-black/20 backdrop-blur-[2px] transition-[opacity,visibility] duration-200 ease-out dark:bg-black/50"
        [ngClass]="fullscreen() ? 'inset-[0.75rem] z-[10001]' : 'top-16 left-0 right-0 bottom-0 z-[100]'"
        [class.opacity-0]="!propertiesPanelOpen()" [class.invisible]="!propertiesPanelOpen()"
        [class.pointer-events-none]="!propertiesPanelOpen()" [class.opacity-100]="propertiesPanelOpen()"
        [class.visible]="propertiesPanelOpen()" (click)="closePropertiesPanel()" aria-hidden="true"></div>
    <aside data-wf-properties-panel
        class="fixed flex w-[520px] flex-col overflow-hidden border-l bg-white/95 backdrop-blur-md transition-transform duration-[380ms] ease-[cubic-bezier(0.32,0.72,0,1)] will-change-transform dark:bg-gray-950/95"
        [ngClass]="[
        fullscreen()
            ? 'top-[0.75rem] right-[0.75rem] bottom-[0.75rem] z-[10002] w-[500px]'
            : 'top-16 right-0 bottom-0 z-[1000]',
        propertiesPanelOpen()
            ? 'border-gray-200/90 dark:border-gray-700/90 shadow-xl shadow-gray-900/10 dark:shadow-2xl dark:shadow-black/50'
            : 'border-transparent shadow-none'
    ]" [class.translate-x-full]="!propertiesPanelOpen()" [class.translate-x-0]="propertiesPanelOpen()"
        [class.pointer-events-none]="!propertiesPanelOpen()" [class.pointer-events-auto]="propertiesPanelOpen()"
        [attr.aria-hidden]="propertiesPanelOpen() ? null : 'true'" [attr.inert]="propertiesPanelOpen() ? null : ''"
        (click)="$event.stopPropagation()">
        <div
            class="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200/80 bg-white/90 dark:border-gray-800/80 dark:bg-gray-900/80 backdrop-blur-sm shrink-0">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <app-icon-v2 name="settings" [size]="16" />
                Node settings
            </h3>
            <button type="button"
                class="inline-flex items-center justify-center p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-400"
                (click)="closePropertiesPanel()" aria-label="Close panel">
                <app-icon-v2 name="x" [size]="18" />
            </button>
        </div>
        <div class="flex-1 min-h-0 overflow-y-auto flex flex-col">
            @if (selectedNode(); as sel) {
            @if (sel.kind === 'START' && sel.startMeta) {
            <div class="flex flex-col min-h-0 flex-1">
                <div
                    class="flex gap-0.5 overflow-x-auto px-2 pt-2 pb-0 border-b border-gray-200/80 dark:border-gray-800/80 shrink-0 bg-white/50 dark:bg-gray-900/40">
                    <button type="button"
                        class="shrink-0 px-3 py-2 text-xs font-semibold rounded-t-lg border transition-colors"
                        [ngClass]="propertiesSettingsTab() === 'routing'
                        ? 'bg-white/95 dark:bg-gray-950/90 border-gray-200/90 dark:border-gray-700/90 border-b-transparent text-violet-700 dark:text-violet-300'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'"
                        (click)="selectPropertiesTab('routing')">
                        Sub-agent conditions
                    </button>
                </div>
                <div class="p-4 flex flex-col gap-4 flex-1 min-h-0">
                    @if (propertiesSettingsTab() === 'general') {
                    <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        This is where the conversation enters your workflow. Drag the violet handle to one or more
                        agents, then use <strong class="font-medium text-gray-800 dark:text-gray-200">Sub-agent
                            conditions</strong> to set how the first step is chosen. This step cannot be removed.
                    </p>
                    <div class="flex flex-col gap-3">
                        <div class="flex flex-col gap-1">
                            <label class="text-xs font-medium text-gray-600 dark:text-gray-400">Name</label>
                            <input type="text" [ngModel]="sel.startMeta.name"
                                (ngModelChange)="patchStartMeta({ name: $event })"
                                class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm" />
                        </div>
                        <div class="flex flex-col gap-1">
                            <label class="text-xs font-medium text-gray-600 dark:text-gray-400">Instructions (system
                                prompt)</label>
                            <textarea rows="5"
                                [ngModel]="sel.startMeta.systemPrompt === null || sel.startMeta.systemPrompt === '' ? '' : sel.startMeta.systemPrompt"
                                (ngModelChange)="patchStartMeta({ systemPrompt: $event === '' ? null : $event })"
                                [placeholder]="'Optional. Guides how the workflow begins.'"
                                class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm resize-y min-h-[100px]"></textarea>
                        </div>
                    </div>
                    }
                    @if (propertiesSettingsTab() === 'routing') {
                    <ng-container *ngTemplateOutlet="subAgentRoutingPanel" />
                    }
                </div>
            </div>
            } @else if (sel.workflow) {
            @if (propertiesNodeDetailsLoading()) {
            <p class="text-sm text-gray-500 dark:text-gray-400 p-4">Loading node details…</p>
            } @else {
            <div class="flex flex-col min-h-0 flex-1">
                <div
                    class="flex gap-0.5 overflow-x-auto px-2 pt-2 pb-0 border-b border-gray-200/80 dark:border-gray-800/80 shrink-0 bg-white/50 dark:bg-gray-900/40">
                    @for (tab of workflowNodeSettingsTabs; track tab.id) {
                    <button type="button"
                        class="shrink-0 px-3 py-2 text-xs font-semibold rounded-t-lg border transition-colors"
                        [ngClass]="propertiesSettingsTab() === tab.id
                        ? 'bg-white/95 dark:bg-gray-950/90 border-gray-200/90 dark:border-gray-700/90 border-b-transparent text-violet-700 dark:text-violet-300'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'"
                        (click)="selectPropertiesTab(tab.id)">
                        {{ tab.label }}
                    </button>
                    }
                </div>
                <div class="p-4 flex flex-col gap-4 flex-1 min-h-0">
                    @switch (propertiesSettingsTab()) {
                    @case ('general') {
                    <div class="flex flex-col gap-12">
                        <div class="flex flex-col gap-1">
                            <label class="text-sm font-semibold text-gray-700 dark:text-gray-400">Name</label>
                            <input type="text" [ngModel]="sel.workflow.name"
                                (ngModelChange)="patchSelectedWorkflow({ name: $event })"
                                class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm" />
                        </div>
                        <div class="flex flex-col gap-4">
                            <div class="flex items-start justify-between gap-3 py-1">
                                <div class="min-w-0">
                                    <p class="text-sm font-semibold text-gray-800 dark:text-gray-300">Override System
                                        Prompt</p>
                                    <p class="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">When enabled, you
                                        will be able to override the entire system prompt for this node instead of just
                                        providing conversation goals.</p>
                                </div>
                                <app-toggle [checked]="sel.workflow.overrideSystemPrompt ?? false"
                                    (toggleChange)="patchSelectedWorkflow({ overrideSystemPrompt: $event })" />
                            </div>
                            <div class="flex flex-col gap-1">
                                <label class="text-sm font-semibold text-gray-700 dark:text-gray-400">Instructions
                                    (system prompt)</label>
                                <textarea rows="6" [ngModel]="sel.workflow.systemPrompt ?? ''"
                                    (ngModelChange)="patchSelectedWorkflow({ systemPrompt: $event })"
                                    placeholder="Extend the system prompt with instructions specific to this conversation node."
                                    class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm resize-y min-h-[120px]"></textarea>
                            </div>
                        </div>
                    </div>
                    }
                    @case ('knowledge') {
                    <div class="flex flex-col flex-1 h-full min-h-0">
                        <app-knowledge-base [botId]="botId() ?? 1" [botName]="botName() ?? ''" [nodeId]="sel.workflow.pid ?? null" (documentUploaded)="handleKnowledgeBaseUpload($event)" />
                    </div>
                    }
                    @case ('llm') {
                    <div class="flex flex-col gap-4">
                        <p class="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                            The <strong class="font-medium text-gray-800 dark:text-gray-200">Agent</strong> tab sets the
                            default AI provider and model for the whole chatbot.
                            Leave the fields below empty to use those defaults for this step, or choose a different
                            provider and model for this agent only.
                        </p>
                        <div class="flex flex-col gap-2 mt-4">
                            <label class="text-xs font-semibold text-gray-700 dark:text-gray-400">Provider</label>
                            <app-dropdown [options]="workflowNodeProviderDropdownOptions()" class="z-100"
                                [selectedValue]="workflowNodeProviderSelectedValue()" selectionMode="single"
                                placeholder="Use chatbot default" [fullWidth]="true" variant="outline" size="sm"
                                chevronIcon="chevronDown" className="w-full relative z-50"
                                (selectionChange)="onWorkflowNodeProviderChange($event)" />
                        </div>
                        <div class="flex flex-col gap-1.5 mt-4">
                            <label class="text-xs font-semibold text-gray-700 dark:text-gray-400">Model</label>
                            @if (modelsLoading() === sel.workflow.providerId) {
                            <p class="text-xs text-gray-500 dark:text-gray-400">Loading models…</p>
                            }
                            @if (sel.workflow.providerId == null) {
                            @if (sel.workflow.modelId != null && hydratingWorkflowProvider()) {
                            <p class="text-xs text-gray-500 dark:text-gray-400">Resolving provider for saved model…</p>
                            } @else if (sel.workflow.modelId != null) {
                            <p class="text-xs text-gray-500 dark:text-gray-400">Choose a provider above, or leave both
                                empty for chatbot defaults.</p>
                            } @else {
                            <p class="text-xs text-gray-500 dark:text-gray-400">Choose a provider first, or leave both
                                empty for chatbot defaults.</p>
                            }
                            }
                            <app-dropdown [options]="workflowNodeModelDropdownOptions()"
                                [selectedValue]="workflowNodeModelSelectedValue()" selectionMode="single"
                                placeholder="Use chatbot default" [fullWidth]="true" variant="outline" size="sm"
                                chevronIcon="chevronDown" className="w-full relative z-50"
                                [class.opacity-50]="sel.workflow.providerId == null"
                                (selectionChange)="onWorkflowNodeModelChange($event)" />
                        </div>
                    </div>
                    }
                    @case ('tools') {
                    <div class="flex flex-col gap-4">
                        <p class="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                            <strong class="font-medium text-gray-800 dark:text-gray-200">Tools</strong> allow this agent to perform actions or query data. Select the tools you want this step to have access to.
                        </p>
                        @if (loadingTools()) {
                            <div class="flex items-center justify-center p-6">
                                <span class="inline-block w-4 h-4 border-2 border-violet-600 border-t-transparent rounded-full animate-spin"></span>
                                <span class="ml-2 text-xs text-gray-500">Loading tools...</span>
                            </div>
                        } @else if (availableTools().length === 0) {
                            <div class="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/40 p-6 text-center">
                                <p class="text-sm font-medium text-gray-800 dark:text-gray-200">No tools available</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Create tools in the main Tools tab first.</p>
                            </div>
                        } @else {
                            <div class="flex flex-col gap-2.5">
                                @for (t of availableTools(); track t.pid) {
                                <label class="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/60 p-3 shadow-sm hover:border-violet-500/50 cursor-pointer transition-colors">
                                    <div class="flex h-5 items-center">
                                        <input type="checkbox"
                                            class="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-600 dark:border-gray-600 dark:bg-gray-800 dark:ring-offset-gray-900"
                                            [checked]="isToolAssignedToSelectedNode(t.pid)"
                                            (change)="toggleToolForSelectedNode(t.pid, $event)" />
                                    </div>
                                    <div class="flex min-w-0 flex-1 flex-col">
                                        <div class="flex items-center gap-2">
                                            <span class="truncate text-sm font-semibold text-gray-900 dark:text-white">{{ t.name }}</span>
                                            @if (t.toolType === 'DATABASE') {
                                                <span class="inline-flex shrink-0 items-center justify-center rounded bg-emerald-100 dark:bg-emerald-950/60 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-700 dark:text-emerald-400">DB</span>
                                            } @else {
                                                <span class="inline-flex shrink-0 items-center justify-center rounded bg-sky-100 dark:bg-sky-950/60 px-1.5 py-0.5 text-[9px] font-bold uppercase text-sky-700 dark:text-sky-400">API</span>
                                            }
                                        </div>
                                        <p class="truncate text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ t.description }}</p>
                                    </div>
                                </label>
                                }
                            </div>
                        }
                    </div>
                    }
                    @case ('routing') {
                    <ng-container *ngTemplateOutlet="subAgentRoutingPanel" />
                    }
                    @case ('schedule') {
                    <div class="flex flex-col gap-4">
                        <p class="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                            Configure a <strong class="font-medium text-gray-800 dark:text-gray-200">cron schedule</strong>
                            to automatically fire this agent on a recurring basis. Save the workflow graph first so the node has a persisted ID.
                        </p>
                        <!-- Name -->
                        <div class="flex flex-col gap-1">
                            <label class="text-xs font-semibold text-gray-700 dark:text-gray-400">
                                Schedule name <span class="text-red-500">*</span>
                            </label>
                            <input type="text" placeholder="e.g. Daily call audit report"
                                [ngModel]="sel.workflow.scheduleDraft?.name ?? ''"
                                (ngModelChange)="patchNodeSchedule({ name: $event })"
                                (focus)="ensureScheduleDraft()"
                                (blur)="markScheduleTouched('name')"
                                [ngClass]="{'!border-red-500 focus:!ring-red-500': scheduleErrors().name && (scheduleTouched()['name'])}"
                                class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm" />
                            @if (scheduleErrors().name && scheduleTouched()['name']) {
                            <p class="text-[10px] text-red-500">{{ scheduleErrors().name }}</p>
                            }
                        </div>
                        <!-- Cron expression -->
                        <div class="flex flex-col gap-1">
                            <label class="text-xs font-semibold text-gray-700 dark:text-gray-400">
                                Cron expression <span class="text-red-500">*</span>
                            </label>
                            <input type="text" placeholder="*/5 * * * * *  (every 5 seconds)"
                                [ngModel]="sel.workflow.scheduleDraft?.cronExpr ?? ''"
                                (ngModelChange)="patchNodeSchedule({ cronExpr: $event })"
                                (focus)="ensureScheduleDraft()"
                                (blur)="markScheduleTouched('cron')"
                                [ngClass]="{'!border-red-500 focus:!ring-red-500': scheduleErrors().cron && (scheduleTouched()['cron'])}"
                                class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm font-mono" />
                            @if (scheduleErrors().cron && scheduleTouched()['cron']) {
                            <p class="text-[10px] text-red-500">{{ scheduleErrors().cron }}</p>
                            }
                            <p class="text-[11px] text-gray-400 dark:text-gray-500">5-field: minute hour day month weekday. 6-field: seconds minute hour day month weekday.</p>
                        </div>
                        <!-- Timezone -->
                        <div class="flex flex-col gap-1">
                            <label class="text-xs font-semibold text-gray-700 dark:text-gray-400">Timezone</label>
                            <input type="text" placeholder="UTC"
                                [ngModel]="sel.workflow.scheduleDraft?.timezone ?? 'UTC'"
                                (ngModelChange)="patchNodeSchedule({ timezone: $event })"
                                class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm" />
                        </div>
                        <!-- Upstream Context -->
                        <div class="flex flex-col gap-1">
                            <label class="text-xs font-semibold text-gray-700 dark:text-gray-400">Include context from</label>
                            <app-dropdown #contextDropdown [options]="upstreamNodeOptions()"
                                [selectedValue]="upstreamNodeSelectedValue()" selectionMode="multiple"
                                placeholder="Choose agents" [fullWidth]="true" variant="outline" size="sm"
                                chevronIcon="chevronDown" className="w-full relative z-50"
                                (selectionChange)="onUpstreamNodeChange($event); contextDropdown.close()" />
                            <p class="text-[11px] text-gray-400 dark:text-gray-500">Injects the latest outputs from the selected agents into the current prompt.</p>
                        </div>
                        <!-- Schedule Action -->
                        <div class="flex flex-col gap-1">
                            <label class="text-xs font-semibold text-gray-700 dark:text-gray-400">
                                Schedule Action <span class="text-red-500">*</span>
                            </label>
                            <textarea rows="4" placeholder="e.g. Generate the daily call audit report for today."
                                [ngModel]="sel.workflow.scheduleDraft?.triggerPrompt ?? ''"
                                (ngModelChange)="patchNodeSchedule({ triggerPrompt: $event })"
                                (focus)="ensureScheduleDraft()"
                                (blur)="markScheduleTouched('action')"
                                [ngClass]="{'!border-red-500 focus:!ring-red-500': scheduleErrors().action && (scheduleTouched()['action'])}"
                                class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm resize-y min-h-[80px]"></textarea>
                            @if (scheduleErrors().action && scheduleTouched()['action']) {
                            <p class="text-[10px] text-red-500">{{ scheduleErrors().action }}</p>
                            }
                            <p class="text-[11px] text-gray-400 dark:text-gray-500">Describe what this agent should do each time the schedule fires. Keep it clear and action-oriented.</p>
                        </div>
                        <!-- Retry policy -->
                        <div class="grid grid-cols-2 gap-3">
                            <div class="flex flex-col gap-1">
                                <label class="text-xs font-semibold text-gray-700 dark:text-gray-400">Max retries</label>
                                <input type="number" min="0" max="10"
                                    [ngModel]="sel.workflow.scheduleDraft?.maxRetries ?? 0"
                                    (ngModelChange)="patchNodeSchedule({ maxRetries: +$event })"
                                    class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm" />
                            </div>
                            <div class="flex flex-col gap-1">
                                <label class="text-xs font-semibold text-gray-700 dark:text-gray-400">Retry interval (min)</label>
                                <input type="number" min="1"
                                    [ngModel]="sel.workflow.scheduleDraft?.retryIntervalMins ?? 5"
                                    (ngModelChange)="patchNodeSchedule({ retryIntervalMins: +$event })"
                                    class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-950 px-2.5 py-1.5 text-sm" />
                            </div>
                        </div>
                        <!-- Active toggle -->
                        <div class="flex items-center justify-between py-1">
                            <div>
                                <p class="text-xs font-semibold text-gray-700 dark:text-gray-300">Enabled</p>
                                <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Disable to pause without deleting the schedule.</p>
                            </div>
                            <app-toggle
                                [checked]="sel.workflow.scheduleDraft?.isActive ?? true"
                                (toggleChange)="patchNodeSchedule({ isActive: $event })" />
                        </div>
                        @if (sel.workflow.scheduleDraft?.pid) {
                        <p class="text-[11px] text-center text-gray-400 dark:text-gray-500">
                            Schedule ID #{{ sel.workflow.scheduleDraft!.pid }} · view run history in the Schedule tab.
                        </p>
                        }
                    </div>
                    }
                    }
                    <div class="pt-2 border-t border-gray-100 dark:border-gray-800 mt-auto">
                        <button type="button" class="text-sm text-red-600 dark:text-red-400 hover:underline"
                            (click)="deleteNode(sel.id)">
                            Delete this node
                        </button>
                    </div>
                </div>
            </div>
            }
            }
            } @else {
            <p class="text-sm text-gray-500 dark:text-gray-400 p-4">No node selected.</p>
            }
        </div>
    </aside>
    <ng-template #subAgentRoutingPanel>
        <div class="flex flex-col gap-4">
            @if (routingEdgeDetailContext(); as rctx) {
            <div class="flex flex-col gap-4">
                <button type="button"
                    class="inline-flex items-center gap-1.5 text-xs font-medium text-violet-600 dark:text-violet-400 hover:underline w-fit"
                    (click)="closeRoutingEdgeDetail()">
                    <app-icon-v2 name="arrow-left" [size]="14" />
                    Back to connections
                </button>
                <div>
                    <h4 class="text-sm font-semibold text-gray-900 dark:text-white leading-snug">
                        {{ rctx.sourceName }} → {{ rctx.targetName }}
                    </h4>
                    @if (selectedNode()?.kind === 'START') {
                    <p class="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                        Forward connection only — rules apply when the workflow enters this sub-agent from Start.
                    </p>
                    } @else {
                    <p class="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                        Forward connection only — rules apply when the flow leaves this agent toward the
                        sub-agent.
                    </p>
                    }
                </div>
                <div class="flex flex-col gap-1.5">
                    <label class="text-xs font-semibold text-gray-700 dark:text-gray-300">Transition
                        type</label>
                    <app-dropdown [options]="edgeTransitionTypeDropdownOptions"
                        [selectedValue]="edgeTransitionSelectedValue(rctx.edge)" selectionMode="single"
                        placeholder="Choose type" [fullWidth]="true" variant="outline" size="sm"
                        chevronIcon="chevronDown" className="w-full relative z-50"
                        (selectionChange)="onEdgeTransitionTypeChange(rctx.edge.id, $event)" />
                </div>
                <div class="flex flex-col gap-1.5">
                    <label class="text-xs font-semibold text-gray-700 dark:text-gray-300">Label</label>
                    <input type="text"
                        class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900/80 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                        [ngModel]="rctx.edge.label ?? ''"
                        (ngModelChange)="patchEdge(rctx.edge.id, { label: $event })"
                        placeholder="Configure condition" />
                </div>
                @if (edgeTransitionSelectedValue(rctx.edge) === 'LLM') {
                <div class="flex flex-col gap-1.5">
                    <label class="text-xs font-semibold text-gray-700 dark:text-gray-300">LLM
                        condition</label>
                    <textarea rows="5"
                        class="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900/80 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 resize-y min-h-[120px]"
                        [ngModel]="rctx.edge.llmConditionDraft ?? ''"
                        (ngModelChange)="patchEdge(rctx.edge.id, { llmConditionDraft: $event })"
                        placeholder="e.g. The user has an issue with billing"></textarea>
                    @if (routingLlmConditionInvalid()) {
                    <p class="text-xs text-red-600 dark:text-red-400">Condition cannot be empty.</p>
                    }
                </div>
                }
                @if (edgeTransitionSelectedValue(rctx.edge) === 'EXPRESSION') {
                <div
                    class="flex flex-col gap-3 rounded-lg border border-gray-200/90 dark:border-gray-700/90 bg-gray-50/80 dark:bg-gray-900/50 p-3">
                    <p class="text-xs font-semibold text-gray-700 dark:text-gray-300">Expression</p>
                    <div class="flex flex-col gap-2">
                        <div class="flex items-center gap-2">
                            <span
                                class="text-xs text-gray-500 dark:text-gray-400 shrink-0 font-mono">(x)</span>
                            <input type="text"
                                class="flex-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900/80 px-2.5 py-1.5 text-sm"
                                [ngModel]="rctx.edge.expressionVariableDraft ?? ''"
                                (ngModelChange)="patchEdge(rctx.edge.id, { expressionVariableDraft: $event })"
                                placeholder="Variable or field name" />
                        </div>
                        <div class="w-40">
                            <app-dropdown [options]="expressionOperatorDropdownOptions"
                                [selectedValue]="expressionOperatorSelectedValue(rctx.edge)"
                                selectionMode="single" placeholder="Operator" [fullWidth]="true"
                                variant="outline" size="sm" chevronIcon="chevronDown"
                                className="w-full relative z-50"
                                (selectionChange)="onExpressionOperatorChange(rctx.edge.id, $event)" />
                        </div>
                        <div class="flex items-center gap-2">
                            <span class="text-xs text-gray-400 dark:text-gray-500 shrink-0">Aa</span>
                            <input type="text"
                                class="flex-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900/80 px-2.5 py-1.5 text-sm"
                                [ngModel]="rctx.edge.expressionValueDraft ?? ''"
                                (ngModelChange)="patchEdge(rctx.edge.id, { expressionValueDraft: $event })"
                                placeholder="Value to compare" />
                        </div>
                    </div>
                    <button type="button" disabled
                        class="self-start rounded-md border border-dashed border-gray-300 dark:border-gray-600 px-2 py-1 text-[11px] text-gray-400 cursor-not-allowed">
                        ( ) Wrap in
                    </button>
                </div>
                }
            </div>
            } @else {
            <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                Configure the order in which outgoing edges are evaluated. Edges higher in the list are
                checked first.
            </p>
            @if (outgoingForwardEdges().length === 0) {
            @if (selectedNode()?.kind === 'START') {
            <p
                class="text-sm text-amber-800 dark:text-amber-200/90 bg-amber-50 dark:bg-amber-950/40 border border-amber-200/80 dark:border-amber-800/60 rounded-lg px-3 py-2">
                Connect Start (violet handle) to one or more agents first. Then set conditions for each forward
                connection.
            </p>
            } @else {
            <p
                class="text-sm text-amber-800 dark:text-amber-200/90 bg-amber-50 dark:bg-amber-950/40 border border-amber-200/80 dark:border-amber-800/60 rounded-lg px-3 py-2">
                Connect this agent to one or more other agents first. Then set conditions for each forward
                connection.
            </p>
            }
            } @else {
            <ul class="flex flex-col gap-2">
                @for (row of outgoingForwardEdges(); track row.edge.id) {
                <li class="group flex items-center gap-3 rounded-lg border border-gray-200/90 dark:border-gray-700/80 bg-white/80 dark:bg-gray-900/40 px-3 py-2.5 shadow-sm"
                    (dragover)="onRoutingEdgeDragOver($event)"
                    (drop)="onRoutingEdgeDrop($event, row.edge.id)">
                    <app-icon-v2 name="git-branch" [size]="18"
                        class="shrink-0 text-gray-400 dark:text-gray-500" />
                    <div class="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-center sm:gap-2">
                        <button type="button"
                            class="text-left text-sm font-medium text-violet-700 dark:text-violet-300 hover:underline truncate"
                            (click)="openRoutingEdgeDetail(row.edge.id)">
                            {{ (row.edge.label && row.edge.label.trim()) ? row.edge.label : 'Configure
                            condition' }}
                        </button>
                        <span class="text-sm text-gray-500 dark:text-gray-400 truncate">→ {{
                            row.targetName }}</span>
                    </div>
                    <button type="button"
                        class="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 dark:hover:text-red-400 cursor-pointer"
                        (click)="deleteRoutingEdge(row.edge.id); $event.stopPropagation()"
                        title="Remove connection" aria-label="Remove connection">
                        <app-icon-v2 name="trash2" [size]="16" />
                    </button>
                    <div draggable="true"
                        class="shrink-0 cursor-grab active:cursor-grabbing text-gray-400 dark:text-gray-500 touch-none"
                        title="Drag to reorder" (dragstart)="onRoutingEdgeDragStart($event, row.edge.id)"
                        (dragend)="onRoutingEdgeDragEnd()">
                        <app-icon-v2 name="grip-vertical" [size]="18" />
                    </div>
                </li>
                }
            </ul>
            }
            }
        </div>
    </ng-template>
</div>